'use client'

import {
    ActivatePaymentAccountData,
    activatePaymentAccount,
} from '@/lib/api/financial/payment-account-api'
import { getCompany } from '@/lib/api/companies/companies-api'
import { fetchPostalCode } from '@/lib/api/fetch-postal-code'
import { isIndividualCompany } from '@/lib/person-type'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdArrowForward,
    MdBadge,
    MdContactPhone,
    MdHome,
    MdSave,
    MdSearch,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { handleDateInputPaste } from '@/lib/utils/date-input'
import CurrencyInput from './currency-input'

// ====== Mascaras (mesmo padrao do checkout/registro) ======
function maskPhone(value: string): string {
    const n = value.replace(/\D/g, '').slice(0, 11)
    if (n.length === 0) return ''
    if (n.length <= 2) return `(${n}`
    if (n.length <= 6) return `(${n.slice(0, 2)}) ${n.slice(2)}`
    if (n.length <= 10)
        return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`
    return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`
}

function maskCep(value: string): string {
    const n = value.replace(/\D/g, '').slice(0, 8)
    if (n.length <= 5) return n
    return `${n.slice(0, 5)}-${n.slice(5)}`
}

function maskCpfCnpj(value: string): string {
    const n = value.replace(/\D/g, '').slice(0, 14)
    if (n.length <= 11) {
        // CPF: 000.000.000-00
        return n
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
    }
    // CNPJ: 00.000.000/0000-00
    return n
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
}

const STEPS = [
    { label: 'Titular', icon: MdBadge },
    { label: 'Contato', icon: MdContactPhone },
    { label: 'Endereço', icon: MdHome },
]

export default function ActivationWizard({
    onActivated,
}: {
    onActivated: () => void
}) {
    const [step, setStep] = useState(0)
    const [activating, setActivating] = useState(false)
    const [loadingCep, setLoadingCep] = useState(false)
    // Faturamento controlado em centavos (acumulo estilo POS do CurrencyInput).
    // Convertido pra reais (Asaas espera reais) so no envio.
    const [incomeCents, setIncomeCents] = useState<number | null>(null)
    const [form, setForm] = useState<ActivatePaymentAccountData>({
        legalName: '',
        cpfCnpj: '',
        birthDate: '',
        mobilePhone: '',
        email: '',
        companyType: 'INDIVIDUAL',
        phone: '',
        address: '',
        province: '',
        postalCode: '',
        addressNumber: 0,
        complement: '',
        incomeValue: 0,
        site: '',
    })

    const { user } = useAuth()

    const set = (patch: Partial<ActivatePaymentAccountData>) =>
        setForm(prev => ({ ...prev, ...patch }))

    const onlyDigits = (v?: string | null) => (v ? v.replace(/\D/g, '') : '')

    // Pré-preenche o KYC com os dados atuais da empresa (e do usuário como
    // fallback). O usuário ainda pode editar tudo antes de ativar.
    useEffect(() => {
        const companyId = (user as any)?.companyId
        if (!companyId) return
        let cancelled = false
        ;(async () => {
            try {
                const res = await getCompany(companyId)
                if (cancelled || !res.success || !res.result) return
                const c = res.result
                const u: any = user
                const individual = isIndividualCompany(c)
                // PF: o documento é o CPF da conta (com o CPF do usuário como
                // fallback para contas criadas antes de o campo existir).
                const cpfCnpj = individual
                    ? onlyDigits(c.cpf || u?.cpf)
                    : onlyDigits(c.cnpj || u?.cnpj)
                setForm(prev => ({
                    ...prev,
                    legalName: c.name || c.tradingName || u?.name || prev.legalName,
                    cpfCnpj: cpfCnpj || prev.cpfCnpj,
                    // A natureza vem do cadastro, não da contagem de dígitos — a
                    // heurística antiga classificava MEI (14 dígitos) como LIMITED.
                    // Entre os tipos de PJ o padrão segue LIMITED, ajustável no select.
                    companyType: individual ? 'INDIVIDUAL' : 'LIMITED',
                    birthDate: u?.birthDate || prev.birthDate,
                    email: c.email || u?.email || prev.email,
                    mobilePhone:
                        onlyDigits(c.whatsappNumber || c.phone || u?.phone) ||
                        prev.mobilePhone,
                    phone: onlyDigits(c.phone) || prev.phone,
                    address: c.street || prev.address,
                    province: c.neighborhood || prev.province,
                    complement: c.complement || prev.complement,
                    postalCode: onlyDigits(c.zipCode) || prev.postalCode,
                    addressNumber: c.number
                        ? Number(onlyDigits(c.number)) || prev.addressNumber
                        : prev.addressNumber,
                    site: c.website || prev.site,
                }))
            } catch {
                // pré-preenchimento é best-effort; sem dados o usuário preenche manualmente
            }
        })()
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(user as any)?.companyId])

    const needsBirthDate =
        form.companyType === 'INDIVIDUAL' || form.companyType === 'MEI'

    const handleCepBlur = async () => {
        const cep = form.postalCode.replace(/\D/g, '')
        if (cep.length !== 8) return
        setLoadingCep(true)
        try {
            const addr = await fetchPostalCode(cep)
            set({
                address: addr.logradouro || form.address,
                province: addr.bairro || form.province,
                complement: addr.complemento || form.complement,
            })
        } catch {
            toast.error('CEP não encontrado. Preencha o endereço manualmente.')
        } finally {
            setLoadingCep(false)
        }
    }

    const validateStep = (s: number): boolean => {
        if (s === 0) {
            if (!form.legalName.trim()) {
                toast.warning('Informe a razão social / nome')
                return false
            }
            if (form.cpfCnpj.replace(/\D/g, '').length < 11) {
                toast.warning('Informe um CPF/CNPJ válido')
                return false
            }
            if (needsBirthDate && !form.birthDate) {
                toast.warning('Data de nascimento é obrigatória para PF/MEI')
                return false
            }
            return true
        }
        if (s === 1) {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
                toast.warning('Informe um email válido')
                return false
            }
            if (form.mobilePhone.replace(/\D/g, '').length < 10) {
                toast.warning('Informe um celular válido com DDD')
                return false
            }
            if (!incomeCents || incomeCents <= 0) {
                toast.warning('Informe o faturamento mensal estimado')
                return false
            }
            return true
        }
        if (s === 2) {
            if (form.postalCode.replace(/\D/g, '').length !== 8) {
                toast.warning('Informe um CEP válido')
                return false
            }
            if (!form.address.trim()) {
                toast.warning('Informe o endereço')
                return false
            }
            if (!form.addressNumber || form.addressNumber <= 0) {
                toast.warning('Informe o número do endereço')
                return false
            }
            if (!form.province.trim()) {
                toast.warning('Informe o bairro')
                return false
            }
            return true
        }
        return true
    }

    const next = () => {
        if (!validateStep(step)) return
        setStep(s => Math.min(s + 1, STEPS.length - 1))
    }
    const back = () => setStep(s => Math.max(s - 1, 0))

    const handleActivate = async () => {
        if (!validateStep(2)) return
        setActivating(true)
        try {
            const payload: ActivatePaymentAccountData = {
                ...form,
                cpfCnpj: form.cpfCnpj.replace(/\D/g, ''),
                postalCode: form.postalCode.replace(/\D/g, ''),
                mobilePhone: form.mobilePhone.replace(/\D/g, ''),
                phone: form.phone
                    ? form.phone.replace(/\D/g, '')
                    : undefined,
                // Vazio -> undefined: string vazia falha no @IsDateString do DTO
                // (so exigido pra PF/MEI; LTDA/Associação enviam sem data).
                birthDate:
                    needsBirthDate && form.birthDate
                        ? form.birthDate
                        : undefined,
                // Asaas espera o faturamento em reais (inteiro).
                incomeValue: incomeCents ? Math.round(incomeCents / 100) : 0,
            }
            const res = await activatePaymentAccount(payload)
            if (res.success) {
                toast.success(
                    'Conta digital criada — aguardando validação do provedor'
                )
                onActivated()
            } else {
                toast.error(res.error || 'Erro ao ativar conta')
            }
        } finally {
            setActivating(false)
        }
    }

    return (
        <div className="card bg-base-100 shadow">
            <div className="card-body">
                <h2 className="card-title">Ativar conta digital</h2>
                <p className="text-sm text-base-content/60">
                    Preencha os dados de KYC em 3 etapas. O provedor analisa e
                    libera o uso (PIX, boleto, cartão).
                </p>

                {/* STEPPER */}
                <ul className="steps w-full my-6">
                    {STEPS.map((s, i) => (
                        <li
                            key={s.label}
                            className={`step ${i <= step ? 'step-secondary' : ''}`}
                            data-content={i < step ? '✓' : `${i + 1}`}
                        >
                            <span className="flex items-center gap-1 text-sm">
                                <s.icon className="hidden sm:inline" />
                                {s.label}
                            </span>
                        </li>
                    ))}
                </ul>

                {/* STEP 0 — TITULAR */}
                {step === 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in">
                        <Field label="Razão social / Nome" className="md:col-span-2">
                            <input
                                className="input input-bordered w-full"
                                value={form.legalName}
                                onChange={e => set({ legalName: e.target.value })}
                                placeholder="Nome do titular ou razão social"
                            />
                        </Field>
                        <Field label="CPF / CNPJ">
                            <input
                                className="input input-bordered w-full"
                                value={maskCpfCnpj(form.cpfCnpj)}
                                onChange={e =>
                                    set({
                                        cpfCnpj: e.target.value.replace(
                                            /\D/g,
                                            ''
                                        ),
                                    })
                                }
                                placeholder="000.000.000-00"
                                inputMode="numeric"
                            />
                        </Field>
                        <Field label="Tipo">
                            <select
                                className="select select-bordered w-full"
                                value={form.companyType}
                                onChange={e =>
                                    set({ companyType: e.target.value as any })
                                }
                            >
                                <option value="INDIVIDUAL">Pessoa Física</option>
                                <option value="MEI">MEI</option>
                                <option value="LIMITED">LTDA</option>
                                <option value="ASSOCIATION">Associação</option>
                            </select>
                        </Field>
                        {needsBirthDate && (
                            <Field label="Data de nascimento">
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={form.birthDate}
                                    onChange={e =>
                                        set({ birthDate: e.target.value })
                                    }
                                    onPaste={e =>
                                        handleDateInputPaste(e, isoDate =>
                                            set({ birthDate: isoDate }),
                                        )
                                    }
                                />
                            </Field>
                        )}
                    </div>
                )}

                {/* STEP 1 — CONTATO */}
                {step === 1 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in">
                        <Field label="Email">
                            <input
                                type="email"
                                className="input input-bordered w-full"
                                value={form.email}
                                onChange={e => set({ email: e.target.value })}
                                placeholder="contato@empresa.com"
                            />
                        </Field>
                        <Field label="Celular (com DDD)">
                            <input
                                className="input input-bordered w-full"
                                value={maskPhone(form.mobilePhone)}
                                onChange={e =>
                                    set({
                                        mobilePhone: e.target.value.replace(
                                            /\D/g,
                                            ''
                                        ),
                                    })
                                }
                                placeholder="(00) 00000-0000"
                                inputMode="numeric"
                            />
                        </Field>
                        <Field label="Telefone fixo (opcional)">
                            <input
                                className="input input-bordered w-full"
                                value={maskPhone(form.phone || '')}
                                onChange={e =>
                                    set({
                                        phone: e.target.value.replace(
                                            /\D/g,
                                            ''
                                        ),
                                    })
                                }
                                placeholder="(00) 0000-0000"
                                inputMode="numeric"
                            />
                        </Field>
                        <Field label="Faturamento mensal estimado">
                            <CurrencyInput
                                className="input input-bordered w-full"
                                valueCents={incomeCents}
                                onChange={setIncomeCents}
                                placeholder="R$ 0,00"
                            />
                        </Field>
                        <Field
                            label="Site institucional (opcional)"
                            className="md:col-span-2"
                        >
                            <input
                                className="input input-bordered w-full"
                                value={form.site || ''}
                                onChange={e => set({ site: e.target.value })}
                                placeholder="https://"
                            />
                        </Field>
                    </div>
                )}

                {/* STEP 2 — ENDERECO */}
                {step === 2 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in">
                        <Field label="CEP">
                            <div className="relative">
                                <input
                                    className="input input-bordered w-full pr-10"
                                    value={maskCep(form.postalCode)}
                                    onChange={e =>
                                        set({
                                            postalCode: e.target.value.replace(
                                                /\D/g,
                                                ''
                                            ),
                                        })
                                    }
                                    onBlur={handleCepBlur}
                                    placeholder="00000-000"
                                    inputMode="numeric"
                                    disabled={loadingCep}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/40">
                                    {loadingCep ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        <MdSearch />
                                    )}
                                </span>
                            </div>
                        </Field>
                        <Field label="Número">
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                value={form.addressNumber || ''}
                                onChange={e =>
                                    set({
                                        addressNumber: Number(e.target.value),
                                    })
                                }
                                placeholder="123"
                            />
                        </Field>
                        <Field label="Endereço" className="md:col-span-2">
                            <input
                                className="input input-bordered w-full"
                                value={form.address}
                                onChange={e => set({ address: e.target.value })}
                                placeholder="Rua, avenida..."
                            />
                        </Field>
                        <Field label="Bairro">
                            <input
                                className="input input-bordered w-full"
                                value={form.province}
                                onChange={e => set({ province: e.target.value })}
                                placeholder="Bairro"
                            />
                        </Field>
                        <Field label="Complemento (opcional)">
                            <input
                                className="input input-bordered w-full"
                                value={form.complement || ''}
                                onChange={e =>
                                    set({ complement: e.target.value })
                                }
                                placeholder="Sala, andar..."
                            />
                        </Field>
                    </div>
                )}

                {/* NAVEGACAO */}
                <div className="card-actions justify-between mt-8">
                    <button
                        className="btn btn-ghost"
                        onClick={back}
                        disabled={step === 0 || activating}
                    >
                        <MdArrowBack /> Voltar
                    </button>
                    {step < STEPS.length - 1 ? (
                        <button className="btn btn-secondary" onClick={next}>
                            Próximo <MdArrowForward />
                        </button>
                    ) : (
                        <button
                            className="btn btn-secondary"
                            onClick={handleActivate}
                            disabled={activating}
                        >
                            {activating ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <MdSave />
                            )}
                            Ativar conta digital
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

function Field({
    label,
    children,
    className,
}: {
    label: string
    children: React.ReactNode
    className?: string
}) {
    return (
        <div className={`form-control w-full ${className || ''}`}>
            <label className="label">
                <span className="label-text">{label}</span>
            </label>
            {children}
        </div>
    )
}
